// Sprint 17 A1: Prioritized Action Queue — "Here's Your Money"
// Ranked deals by revenue × probability × urgency, one-click actions
import { useState } from "react";
import { Link } from "react-router";
import {
  Flame, Clock, Snowflake, Phone, Mail, MessageSquare,
  ChevronDown, ChevronUp,
  Brain, Zap, AlertTriangle,
} from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import type { PrioritizedDeal, TodayIntelligence } from "@/hooks/use-crm-today";
import { formatCurrency } from "@/lib/i18n";
import { normalizeCrmActionHref } from "@ease/crm/safe-action-hrefs";

const MOMENTUM_ICONS = {
  accelerating: { Icon: Flame, color: "text-[var(--color-accent-action)]", label: "Hot" },
  stable: { Icon: Clock, color: "text-[var(--color-warning)]", label: "Stable" },
  decelerating: { Icon: Snowflake, color: "text-[var(--color-info)]", label: "Cold" },
} as const;

const SECTIONS = [
  { key: "closingToday" as const, label: "Close Today", accent: "border-l-[var(--color-success)]" },
  { key: "newLeads" as const, label: "New Leads", accent: "border-l-[var(--color-primary)]" },
  { key: "followUp" as const, label: "Follow Up", accent: "border-l-[var(--color-warning)]" },
  { key: "overdue" as const, label: "Overdue", accent: "border-l-[var(--color-destructive)]" },
];

interface ActionQueueProps {
  data: TodayIntelligence;
}

export function ActionQueue({ data }: ActionQueueProps) {
  return (
    <div className="space-y-6">
      {SECTIONS.map(({ key, label, accent }) => {
        const deals = data[key];
        return (
          <div key={key}>
            <h3 className="text-xs font-semibold text-[var(--color-muted-foreground)] uppercase tracking-wide mb-3">
              {label}
              <span className="ml-2 font-normal">({deals.length})</span>
            </h3>

            {deals.length === 0 ? (
              <div className="text-sm text-[var(--color-muted-foreground)] py-4 text-center border border-dashed border-[var(--color-border)] rounded-lg">
                {key === "closingToday" ? "No deals closing today" :
                 key === "newLeads" ? "No new leads — great follow-up!" :
                 key === "overdue" ? "Nothing overdue" :
                 "No deals to follow up"}
              </div>
            ) : (
              <div className="space-y-2">
                {deals.map((deal) => (
                  <DealCard
                    key={deal.id}
                    deal={deal}
                    accentBorder={accent}
                    isNewLead={key === "newLeads"}
                    isOverdue={key === "overdue"}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DealCard({
  deal,
  accentBorder,
  isNewLead,
  isOverdue,
}: {
  deal: PrioritizedDeal;
  accentBorder: string;
  isNewLead: boolean;
  isOverdue: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const momentum = MOMENTUM_ICONS[deal.momentumDirection];
  const MomentumIcon = momentum.Icon;
  const ExpandIcon = expanded ? ChevronUp : ChevronDown;

  const formattedValue = formatCurrency(deal.value, deal.currency || "EUR", {
    maximumFractionDigits: 0,
  });
  const phoneHref = deal.contactPhone ? normalizeCrmActionHref(`tel:${deal.contactPhone}`) : null;
  const smsHref = deal.contactPhone ? normalizeCrmActionHref(`sms:${deal.contactPhone}`) : null;
  const emailHref = deal.contactEmail ? normalizeCrmActionHref(`mailto:${deal.contactEmail}`) : null;

  return (
    <div className={`stagger-item border border-[var(--color-border)] border-l-4 ${accentBorder} rounded-lg bg-[var(--color-card)] px-4 py-3 transition-shadow hover:shadow-md`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {/* Momentum indicator */}
          <div className={`flex items-center gap-1 ${momentum.color}`} title={`${momentum.label} (${deal.momentumScore})`}>
            <MomentumIcon className="h-4 w-4" />
          </div>

          <div className="min-w-0">
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-sm font-medium text-[var(--color-foreground)] truncate hover:text-[var(--color-primary)] block text-left"
            >
              {deal.title}
            </button>
            <Link
              to={`/crm/contacts/${deal.contactId}`}
              className="text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-primary)]"
            >
              {deal.contactName}
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-4 text-right flex-shrink-0">
          <div>
            <p className="text-sm font-semibold text-[var(--color-foreground)]">{formattedValue}</p>
            <p className="text-xs text-[var(--color-muted-foreground)]">{deal.probability}% prob</p>
          </div>

          {/* New lead speed alert */}
          {isNewLead && (
            <div className="text-right">
              <p className="text-xs font-medium text-[var(--color-success)]">
                <Zap className="h-3 w-3 inline mr-0.5" />
                {Math.floor((Date.now() - new Date(deal.createdAt).getTime()) / 60000)}m ago
              </p>
              <p className="text-[10px] text-[var(--color-success)]">5 min = 21x close</p>
            </div>
          )}

          {/* Overdue indicator */}
          {isOverdue && (
            <div className="text-right">
              <p className="text-xs font-medium text-[var(--color-destructive)]">
                <AlertTriangle className="h-3 w-3 inline mr-0.5" />
                {deal.daysSinceLastActivity}d overdue
              </p>
            </div>
          )}

          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
            aria-label={expanded ? "Collapse deal" : "Expand deal"}
          >
            <ExpandIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* AI next-action */}
      {deal.nextAction && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[10px] font-medium text-[var(--color-ai)] bg-[var(--color-ai-light)] px-1.5 py-0.5 rounded border border-[var(--color-ai-border)]">
            <Brain className="h-2.5 w-2.5 inline mr-0.5" />AI
          </span>
          <span className="text-xs text-[var(--color-foreground)]">{deal.nextAction}</span>
          {deal.nextActionConfidence != null && (
            <span className="text-[10px] text-[var(--color-muted-foreground)]">
              {Math.round(deal.nextActionConfidence * 100)}%
            </span>
          )}
        </div>
      )}

      {/* Expanded: one-click action shortcuts */}
      {expanded && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-[var(--color-border)]">
          {phoneHref && (
            <a
              href={phoneHref}
              aria-label={`Call ${deal.contactName}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-foreground)] hover:bg-[var(--color-primary-light)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary-border)] transition-colors"
            >
              <Phone className="h-3 w-3" /> Call
            </a>
          )}
          {emailHref && (
            <a
              href={emailHref}
              aria-label={`Email ${deal.contactName}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-foreground)] hover:bg-[var(--color-primary-light)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary-border)] transition-colors"
            >
              <Mail className="h-3 w-3" /> Email
            </a>
          )}
          {smsHref && (
            <a
              href={smsHref}
              aria-label={`Text ${deal.contactName}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-foreground)] hover:bg-[var(--color-primary-light)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary-border)] transition-colors"
            >
              <MessageSquare className="h-3 w-3" /> SMS
            </a>
          )}
          <Link
            to={`/crm/deals/${deal.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-[var(--color-primary)] text-[var(--color-primary-foreground)] hover:bg-[var(--color-primary-hover)] transition-colors ml-auto"
          >
            View Deal
          </Link>
        </div>
      )}
    </div>
  );
}

// Skeleton for loading state
export function ActionQueueSkeleton() {
  return (
    <div className="space-y-6">
      {[1, 2, 3, 4].map((section) => (
        <div key={section}>
          <Skeleton className="mb-3 h-3 w-24" />
          <div className="space-y-2">
            {Array.from({ length: section === 1 ? 2 : 1 }).map((_, i) => (
              <SkeletonCard key={i} className="space-y-2" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
